<main-layout [selected]="'FINANCE'">
    <content class="w-full flex overflow-scroll">
        <div class="finance-main-container">
            <div class="title-text">
                {{ "finance_page_title" | translate }}
            </div>

            <div class="finance-actions-container">
                <ng-container *ngIf="(restaurantData$ | async)?.stripeAccountId; else onlyCreateButton">
                    <fs-button [text]="'finance_refresh' | translate"
                               [type]="'raised'"
                               [leadingIcon]="'refresh'"
                               [disabled]="(isLoadingFinancialData$ | async) || false"
                               (onPress)="refreshFinancialStatus()">
                    </fs-button>

                    <fs-button [text]="'finance_open_stripe_onboarding' | translate"
                               [type]="'raised-primary'"
                               [leadingIcon]="'account_balance'"
                               [disabled]="(isLoadingStripeSession$ | async) || false"
                               (onPress)="onOpenStripeOnboarding()">
                    </fs-button>
                </ng-container>

                <ng-template #onlyCreateButton>
                    <fs-button [text]="'finance_create_stripe' | translate"
                               [type]="'raised-primary'"
                               [leadingIcon]="'add'"
                               [disabled]="(isLoadingCreateStripeID$ | async) || false"
                               (onPress)="onPressCreate()">
                    </fs-button>
                </ng-template>
            </div>

            <ng-container *ngIf="(restaurantData$ | async)?.stripeAccountId; else stripeNotConnected">
                <div class="status-summary" [ngClass]="getFinancialStatusClass((financialData$ | async)?.financialStatus)">
                    <div class="status-summary-label">
                        {{ "finance_status_label" | translate }}
                    </div>
                    <div class="status-summary-value">
                        {{ getFinancialStatusKey((financialData$ | async)?.financialStatus) | translate }}
                    </div>
                    <div class="status-summary-meta">
                        <div>
                            {{ "finance_stripe_account_id" | translate }}:
                            <strong>{{ (financialData$ | async)?.stripeAccountId }}</strong>
                        </div>
                        <div>
                            {{ "finance_currency" | translate }}:
                            <strong>{{ (financialData$ | async)?.currency || "-" }}</strong>
                        </div>
                    </div>
                </div>

                <div *ngIf="(isLoadingFinancialData$ | async)" class="loading-finance-data">
                    <fs-loading-spinner [variant]="LOADING_STYLE.CIRCLE_GRAY"></fs-loading-spinner>
                </div>

                <ng-container *ngIf="!(isLoadingFinancialData$ | async)">
                    <div class="section-title">
                        {{ "finance_balance_section_title" | translate }}
                    </div>
                    <div class="balances-grid">
                        <div class="balance-card">
                            <div class="balance-label">{{ "finance_available_balance" | translate }}</div>
                            <div class="balance-value"
                                 [ngClass]="{ 'is-negative': (((financialData$ | async)?.availableBalanceCents || 0) < 0) }">
                                {{ ((financialData$ | async)?.availableBalanceCents || 0) | format_cents: true }}
                            </div>
                            <div class="balance-currency">{{ (financialData$ | async)?.currency || "-" }}</div>
                        </div>

                        <div class="balance-card">
                            <div class="balance-label">{{ "finance_pending_balance" | translate }}</div>
                            <div class="balance-value">
                                {{ ((financialData$ | async)?.pendingBalanceCents || 0) | format_cents: true }}
                            </div>
                            <div class="balance-currency">{{ (financialData$ | async)?.currency || "-" }}</div>
                        </div>

                        <div class="balance-card">
                            <div class="balance-label">{{ "finance_total_balance" | translate }}</div>
                            <div class="balance-value"
                                 [ngClass]="{ 'is-negative': (((financialData$ | async)?.totalBalanceCents || 0) < 0) }">
                                {{ ((financialData$ | async)?.totalBalanceCents || 0) | format_cents: true }}
                            </div>
                            <div class="balance-currency">{{ (financialData$ | async)?.currency || "-" }}</div>
                        </div>
                    </div>

                    <div class="section-title">
                        {{ "finance_capabilities_section_title" | translate }}
                    </div>
                    <div class="status-table">
                        <div class="status-row">
                            <div class="status-row-label">{{ "finance_has_negative_balance" | translate }}</div>
                            <div class="status-row-value"
                                 [ngClass]="((financialData$ | async)?.hasNegativeBalance || false) ? 'no' : 'yes'">
                                <mat-icon class="status-row-icon">
                                    {{ ((financialData$ | async)?.hasNegativeBalance || false) ? 'warning' : 'check_circle' }}
                                </mat-icon>
                                {{ getBooleanValueKey(!((financialData$ | async)?.hasNegativeBalance || false)) | translate }}
                            </div>
                        </div>

                        <div class="status-row">
                            <div class="status-row-label">{{ "finance_charges_enabled" | translate }}</div>
                            <div class="status-row-value"
                                 [ngClass]="((financialData$ | async)?.chargesEnabled || false) ? 'yes' : 'no'">
                                <mat-icon class="status-row-icon">
                                    {{ ((financialData$ | async)?.chargesEnabled || false) ? 'check_circle' : 'cancel' }}
                                </mat-icon>
                                {{ getBooleanValueKey((financialData$ | async)?.chargesEnabled || false) | translate }}
                            </div>
                        </div>

                        <div class="status-row">
                            <div class="status-row-label">{{ "finance_payouts_enabled" | translate }}</div>
                            <div class="status-row-value"
                                 [ngClass]="((financialData$ | async)?.payoutsEnabled || false) ? 'yes' : 'no'">
                                <mat-icon class="status-row-icon">
                                    {{ ((financialData$ | async)?.payoutsEnabled || false) ? 'check_circle' : 'cancel' }}
                                </mat-icon>
                                {{ getBooleanValueKey((financialData$ | async)?.payoutsEnabled || false) | translate }}
                            </div>
                        </div>

                        <div class="status-row">
                            <div class="status-row-label">{{ "finance_details_submitted" | translate }}</div>
                            <div class="status-row-value"
                                 [ngClass]="((financialData$ | async)?.detailsSubmitted || false) ? 'yes' : 'no'">
                                <mat-icon class="status-row-icon">
                                    {{ ((financialData$ | async)?.detailsSubmitted || false) ? 'check_circle' : 'cancel' }}
                                </mat-icon>
                                {{ getBooleanValueKey((financialData$ | async)?.detailsSubmitted || false) | translate }}
                            </div>
                        </div>
                    </div>

                    <div class="section-title">
                        {{ "finance_requirements_section_title" | translate }}
                    </div>
                    <div class="requirements-grid">
                        <div class="requirements-card">
                            <div class="requirements-title">{{ "finance_currently_due" | translate }}</div>
                            <ng-container *ngIf="((financialData$ | async)?.currentlyDue || []).length > 0; else noCurrentlyDue">
                                <div class="requirements-item"
                                     *ngFor="let field of ((financialData$ | async)?.currentlyDue || [])">
                                    {{ formatRequirementValue(field) }}
                                </div>
                            </ng-container>
                            <ng-template #noCurrentlyDue>
                                <div class="requirements-empty">{{ "finance_no_currently_due" | translate }}</div>
                            </ng-template>
                        </div>

                        <div class="requirements-card">
                            <div class="requirements-title">{{ "finance_pending_verification" | translate }}</div>
                            <ng-container *ngIf="((financialData$ | async)?.pendingVerification || []).length > 0; else noPendingVerification">
                                <div class="requirements-item"
                                     *ngFor="let field of ((financialData$ | async)?.pendingVerification || [])">
                                    {{ formatRequirementValue(field) }}
                                </div>
                            </ng-container>
                            <ng-template #noPendingVerification>
                                <div class="requirements-empty">{{ "finance_no_pending_verification" | translate }}</div>
                            </ng-template>
                        </div>
                    </div>

                    <div class="section-title">
                        {{ "finance_disabled_reason" | translate }}
                    </div>
                    <div class="disabled-reason-box">
                        {{ (financialData$ | async)?.disabledReason || ("finance_not_available" | translate) }}
                    </div>
                </ng-container>

                <ng-container *ngIf="(showStripeSession$ | async)">
                    <div class="section-title stripe-onboarding-title">
                        {{ "finance_stripe_onboarding_title" | translate }}
                    </div>
                    <div class="stripe-onboarding-box">
                        <div *ngIf="(isLoadingStripeSession$ | async)" class="loading-finance-data is-small">
                            <fs-loading-spinner [variant]="LOADING_STYLE.CIRCLE_GRAY"></fs-loading-spinner>
                        </div>
                        <div id="stripe-session-load-container"></div>
                    </div>
                </ng-container>
            </ng-container>

            <ng-template #stripeNotConnected>
                <div class="stripe-missing-container">
                    <div class="stripe-missing-title">
                        {{ "finance_not_connected_title" | translate }}
                    </div>
                    <div class="stripe-missing-subtitle">
                        {{ "finance_not_connected_description" | translate }}
                    </div>
                </div>
            </ng-template>
        </div>
    </content>
</main-layout>
